import React, { useState, useEffect } from 'react';
import Header from '../Header';
import Footer from '../Footer';
import '../../styles/CommonStyles.css';
import { PUBLIC_URLS, API_BASE_URL } from '../../components/config/apiConfig';

function GalleryPage() {
  const [activeTab, setActiveTab] = useState('photos');
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };
  
  // Function to generate YouTube thumbnail URL
  const getYouTubeThumbnail = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : '';
  };
  
  // Function to generate a valid image URL - fixed for correct path structure
  const getValidImageUrl = (item) => {
    if (!item) return '';
    
    // Handle YouTube videos
    if (item.media_type === 'video' && item.url) {
      return getYouTubeThumbnail(item.url);
    }
    
    // For images, check thumbnail_url first
    if (item.thumbnail_url) {
      // If it's already a full URL, use it directly
      if (item.thumbnail_url.startsWith('http')) {
        return item.thumbnail_url;
      }
      
      // Fix the path to include 'static' for local files
      let path = item.thumbnail_url;
      
      // Remove the leading slash if present
      if (path.startsWith('/')) {
        path = path.substring(1);
      }
      
      // If path contains 'uploads/gallery', replace with 'static/uploads/gallery'
      if (path.includes('uploads/gallery') && !path.includes('static/')) {
        path = path.replace('uploads/gallery', 'static/uploads/gallery');
      }
      
      // Construct the full URL
      const baseUrl = API_BASE_URL.substring(0, API_BASE_URL.lastIndexOf('/api')); // Remove the '/api' part
      return `${baseUrl}/${path}`;
    }
    
    // If no thumbnail, try using the main URL
    if (item.url) {
      // If it's already a full URL, use it directly
      if (item.url.startsWith('http')) {
        return item.url;
      }
      
      // Fix the path to include 'static' for local files
      let path = item.url;
      
      // Remove the leading slash if present
      if (path.startsWith('/')) {
        path = path.substring(1);
      }
      
      // If path contains 'uploads/gallery', replace with 'static/uploads/gallery'
      if (path.includes('uploads/gallery') && !path.includes('static/')) {
        path = path.replace('uploads/gallery', 'static/uploads/gallery');
      }
      
      // Construct the full URL
      const baseUrl = API_BASE_URL.substring(0, API_BASE_URL.lastIndexOf('/api')); // Remove the '/api' part
      return `${baseUrl}/${path}`;
    }
    
    // Fallback to empty string if no valid URLs found
    return '';
  };
  
  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Function to handle image loading errors with better debug info
  const handleImageError = (e) => {
    console.error('Image failed to load:', e.target.src);
    
    // Try an alternative path with /static/ included
    const src = e.target.src;
    if (!src.includes('/static/') && src.includes('/uploads/')) {
      const newSrc = src.replace('/uploads/', '/static/uploads/');
      e.target.src = newSrc;
    } else {
      // Set a default placeholder image
      e.target.src = '/static/images/placeholder-image.jpg';
    }
  };

  // Function to fetch gallery items
  const fetchGalleryItems = async (mediaType, page = 1) => {
    setLoading(true);
    try {
      // Use the correct endpoints for photos and videos
      const endpoint = mediaType === 'image' 
        ? PUBLIC_URLS.GALLERY_PHOTOS
        : PUBLIC_URLS.GALLERY_VIDEOS;
            
      const response = await fetch(`${endpoint}?page=${page}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch ${mediaType} items: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (!data.items || !Array.isArray(data.items)) {
        console.error('Invalid data format - items is not an array:', data);
        throw new Error('Invalid data format received from server');
      }
      
      if (mediaType === 'image') {
        setPhotos(currentPage === 1 ? data.items : [...photos, ...data.items]);
      } else {
        setVideos(currentPage === 1 ? data.items : [...videos, ...data.items]);
      }
      
      setTotalPages(data.total_pages || 1);
      setLoading(false);
    } catch (error) {
      console.error(`Error fetching ${mediaType} items:`, error);
      if (mediaType === 'image') {
        setPhotos([]);
      } else {
        setVideos([]);
      }
      setLoading(false);
    }
  };
  
  // Load more items
  const handleLoadMore = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchGalleryItems(activeTab === 'photos' ? 'image' : 'video', nextPage);
    }
  };
  
  // Effect to fetch data when tab changes
  useEffect(() => {
    setCurrentPage(1);
    const mediaType = activeTab === 'photos' ? 'image' : 'video';
    fetchGalleryItems(mediaType, 1);
  }, [activeTab]);

  return (
    <div className="page-container">
      <Header />
      
      <div className="page-header">
        <h1>GALLERY & VIDEOS</h1>
        <p>Explore photos and videos from our events and programs</p>
      </div>
      
      <section className="content-section">
        <div className="tabs" style={{ 
          display: 'flex', 
          justifyContent: 'center',
          marginBottom: '2rem'
        }}>
          <button 
            className={`tab ${activeTab === 'photos' ? 'active' : ''}`}
            onClick={() => setActiveTab('photos')}
            style={{ 
              padding: '0.8rem 2rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '4px',
              marginRight: '1rem',
              cursor: 'pointer',
              backgroundColor: activeTab === 'photos' ? 'var(--primary-color)' : '#f0f0f0',
              color: activeTab === 'photos' ? 'white' : 'var(--primary-color)'
            }}
          >
            PHOTOS
          </button>
          <button 
            className={`tab ${activeTab === 'videos' ? 'active' : ''}`}
            onClick={() => setActiveTab('videos')}
            style={{ 
              padding: '0.8rem 2rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: activeTab === 'videos' ? 'var(--primary-color)' : '#f0f0f0',
              color: activeTab === 'videos' ? 'white' : 'var(--primary-color)'
            }}
          >
            VIDEOS
          </button>
        </div>
        
        {activeTab === 'photos' && (
          <div className="photos-container">
            <h2>EVENT PHOTOS</h2>
            <p>
              Browse through photos from our various events, workshops, and programs. 
              These images capture the vibrant community of valuation professionals 
              coming together to learn, network, and grow.
            </p>
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
            ) : (
              <>
                {photos.length > 0 ? (
                  <div className="photos-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
                    gap: '1.5rem',
                    marginTop: '2rem'
                  }}>
                    {photos.map(photo => {
                      const imageUrl = getValidImageUrl(photo);
                      return (
                        <div className="photo-card" key={photo.id} style={{ 
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          boxShadow: 'var(--box-shadow)'
                        }}>
                          {/* Use an actual img element instead of background-image for better error handling */}
                          <div className="photo-image" style={{ 
                            height: '200px',
                            overflow: 'hidden',
                            position: 'relative'
                          }}>
                            <img 
                              src={imageUrl}
                              alt={photo.title}
                              onError={handleImageError}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center',
                                cursor: 'pointer'
                              }}
                            />
                          </div>
                          <div className="photo-content" style={{ padding: '1rem' }}>
                            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.3rem', color: 'var(--primary-color)' }}>
                              {photo.title}
                            </h3>
                            <p style={{ fontSize: '0.9rem', color: '#666' }}>
                              {formatDate(photo.event_date)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    No photos available at the moment.
                  </div>
                )}
                
                {photos.length > 0 && currentPage < totalPages && (
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: '2rem' 
                  }}>
                    <button 
                      className="btn btn-primary" 
                      style={{ 
                        backgroundColor: 'var(--primary-color)', 
                        color: 'white' 
                      }}
                      onClick={handleLoadMore}
                    >
                      LOAD MORE
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        
        {activeTab === 'videos' && (
          <div className="videos-container">
            <h2>EDUCATIONAL VIDEOS</h2>
            <p>
              Watch recordings of our workshops, webinars, and educational sessions 
              conducted by industry experts. These videos are a valuable resource for 
              valuation professionals looking to enhance their knowledge and skills.
            </p>
            
            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
            ) : (
              <>
                {videos.length > 0 ? (
                  <div className="videos-grid" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
                    gap: '2rem',
                    marginTop: '2rem'
                  }}>
                    {videos.map(video => {
                      const imageUrl = getValidImageUrl(video);
                      return (
                        <div className="video-card" key={video.id} style={{ 
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          boxShadow: 'var(--box-shadow)'
                        }}>
                          <div className="video-thumbnail" style={{ 
                            position: 'relative',
                            height: '200px',
                            overflow: 'hidden'
                          }}>
                            <img 
                              src={imageUrl}
                              alt={video.title}
                              onError={handleImageError}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'center'
                              }}
                            />
                            <div className="play-button" style={{ 
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              width: '60px',
                              height: '60px',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              borderRadius: '50%',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center'
                            }}>
                              <div style={{ 
                                width: '0',
                                height: '0',
                                borderTop: '10px solid transparent',
                                borderBottom: '10px solid transparent',
                                borderLeft: '18px solid white',
                                marginLeft: '5px'
                              }}></div>
                            </div>
                          </div>
                          <div className="video-content" style={{ padding: '1rem' }}>
                            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.3rem', color: 'var(--primary-color)' }}>
                              {video.title}
                            </h3>
                            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                              {video.description || "Educational Video"}
                            </p>
                            <a 
                              href={video.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn btn-primary" 
                              style={{ 
                                display: 'block',
                                width: '100%', 
                                textAlign: 'center',
                                textDecoration: 'none'
                              }}
                            >
                              WATCH VIDEO
                            </a>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    No videos available at the moment.
                  </div>
                )}
                
                {videos.length > 0 && currentPage < totalPages && (
                  <div style={{ 
                    textAlign: 'center', 
                    marginTop: '2rem' 
                  }}>
                    <button 
                      className="btn btn-primary" 
                      style={{ 
                        backgroundColor: 'var(--primary-color)', 
                        color: 'white' 
                      }}
                      onClick={handleLoadMore}
                    >
                      LOAD MORE
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </section>
      
      <Footer />
    </div>
  );
}

export default GalleryPage;